/**
 * 💎 Trial Banner
 *
 * Banner تحذيري يظهر في أعلى الـ Dashboard للـ providers في trial.
 *
 * Behavior:
 *   - trial_active (> 7 أيام): banner أخضر "باقي X يوم"
 *   - trial_ending_soon (≤ 7): banner أصفر "اشترك قريباً"
 *   - trial_expired: ما يظهر (الـ SubscriptionGuard يحوّل لـ /subscription)
 *   - paid_active: ما يظهر
 *
 * يستخدم real-time subscription لـ provider doc.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, Crown, Sparkles, X } from 'lucide-react';

import { subscriptionService } from '@application/services/SubscriptionService';
import type { ProviderSubscription } from '@application/services/SubscriptionService';
import { useUser } from '@application/stores/auth.store';
import { cn } from '@shared/utils/cn';

const DISMISS_KEY = 'havlaa-trial-banner-dismissed';
const DISMISS_DURATION_HOURS = 12;

export function TrialBanner() {
  const user = useUser();
  const [subscription, setSubscription] = useState<ProviderSubscription | null>(
    null
  );
  const [dismissed, setDismissed] = useState(() => isDismissed());

  // اشتراك real-time على subscription لو الـ user provider
  useEffect(() => {
    if (!user?.providerId) {
      setSubscription(null);
      return;
    }

    const unsub = subscriptionService.subscribeToProviderSubscription(
      user.providerId,
      (sub) => setSubscription(sub),
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('[TrialBanner] subscription error', err);
      }
    );

    return unsub;
  }, [user?.providerId]);

  // ما نعرض الـ banner للـ:
  //   - non-provider users (admin/vendor)
  //   - لو ما في subscription
  //   - لو paid_active (مش في trial)
  //   - لو expired (الـ SubscriptionGuard بيحوّل)
  //   - لو dismissed مؤقتاً
  if (!user?.providerId) return null;
  if (!subscription) return null;
  if (subscription.status === 'paid_active') return null;
  if (subscription.isExpired) return null;
  if (dismissed) return null;

  const isEndingSoon = subscription.isEndingSoon;
  const days = subscription.daysRemaining;

  const handleDismiss = () => {
    setDismissedFor(DISMISS_DURATION_HOURS);
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className={cn(
          'sticky top-0 z-40 w-full border-b shadow-sm backdrop-blur-md',
          isEndingSoon
            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-amber-200'
            : 'bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border-emerald-200'
        )}
      >
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          {/* Icon + Message */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={cn(
                'grid place-items-center h-9 w-9 rounded-xl shrink-0 shadow-sm',
                isEndingSoon
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
              )}
            >
              {isEndingSoon ? (
                <Clock className="h-4 w-4" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'text-sm font-bold truncate',
                  isEndingSoon ? 'text-amber-900' : 'text-emerald-900'
                )}
              >
                {isEndingSoon
                  ? `⏰ باقي ${days} ${days === 1 ? 'يوم' : 'أيام'} في فترتك التجريبية!`
                  : `🎉 مرحباً! باقي ${days} يوم في فترتك التجريبية`}
              </p>
              <p
                className={cn(
                  'text-[11px] truncate hidden sm:block',
                  isEndingSoon ? 'text-amber-700' : 'text-emerald-700'
                )}
              >
                {isEndingSoon
                  ? 'اشترك الآن لتجنّب فقدان العملاء.'
                  : 'استمتع بكل ميزات Havlaa، واشترك قبل النهاية للحصول على وصول دائم.'}
              </p>
            </div>
          </div>

          {/* Action */}
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/subscription"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-bold shadow-md transition-all hover:-translate-y-0.5',
                isEndingSoon
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-lg shadow-amber-200/40'
                  : 'bg-gradient-to-r from-brand-purple-600 to-brand-gold-500 text-white hover:shadow-lg shadow-brand-purple-200/40'
              )}
            >
              <Crown className="h-3.5 w-3.5" />
              <span>اشترك الآن</span>
            </Link>

            <button
              type="button"
              onClick={handleDismiss}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                isEndingSoon
                  ? 'text-amber-700 hover:bg-amber-200/60'
                  : 'text-emerald-700 hover:bg-emerald-200/60'
              )}
              title="إخفاء مؤقتاً"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────
// Dismissal logic (localStorage)
// ─────────────────────────────────────────────────────

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const expiresAt = parseInt(raw, 10);
    return !isNaN(expiresAt) && expiresAt > Date.now();
  } catch {
    return false;
  }
}

function setDismissedFor(hours: number): void {
  try {
    const expiresAt = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(expiresAt));
  } catch {
    // ignore
  }
}
